import { collection, query, where, getDocs, doc, getDoc, setDoc, updateDoc, arrayUnion, arrayRemove } from 'firebase/firestore';
import { db, functions } from '../firebase';
import { useState, useEffect } from 'react';
import { httpsCallable } from "firebase/functions";
import { getAuth, sendPasswordResetEmail } from 'firebase/auth';
import { useNavigate, useParams } from 'react-router-dom';
import { UseHubName } from '../Hooks/UseHubName';
import '../Styles/Config.css';

const HubConfig = () => {
  const { hubId } = useParams();
  const auth = getAuth();
  const [correo, setCorreo] = useState('');
  const [mensaje, setMensaje] = useState('');
  const [usuarios, setUsuarios] = useState([]);
  const [hasCalendar, setHasCalendar] = useState(true);
  const [hasMap, setHasMap] = useState(true);
  const [hasDocs, setHasDocs] = useState(true);
  const [hasPlans, setHasPlans] = useState(true);
  const [hasComms, setHasComms] = useState(true);
  const navigate = useNavigate();

  const hubName = UseHubName(hubId);

  const PERMISOS_DISPONIBLES = [
    { clave: 'verDashboard', label: 'Dashboard' },
    { clave: 'verDocs', label: 'Documentación' },
    { clave: 'verAccPlan', label: 'Acciones Planificadas' },
  ];

  useEffect(() => {
    const cargarDatos = async () => {
      try {
        if (!hubId) return;

        // 1. Obtener usuarios del hub
        const usuariosRef = collection(db, 'usuarios');
        const q = query(
          usuariosRef,
          where('hubId', 'array-contains-any', [hubId, '__superadmin__'])
        );
        const querySnapshot = await getDocs(q);
        const usuariosList = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
        }));

        // 2. Obtener configuración del hub
        const hubDocRef = doc(db, 'private-hubs', hubId);
        const hubDocSnap = await getDoc(hubDocRef);
        let hubData = {};
        if (hubDocSnap.exists()) {
          hubData = hubDocSnap.data();
          setHasCalendar(hubData.hasCalendar ?? true);
          setHasMap(hubData.hasMap ?? false);
          setHasDocs(hubData.hasDocs ?? false);
          setHasPlans(hubData.hasPlans ?? false);
          setHasComms(hubData.hasComms ?? false);
        } else {
          setHasCalendar(true);
          setHasMap(true);
          setHasDocs(true);
          setHasPlans(true);
          setHasComms(true);
        }

        // 3. Marcar superusuarios
        const superUsers = Array.isArray(hubData.superUsers) ? hubData.superUsers : [];
        const usuariosMarcados = usuariosList.map(u => ({
          ...u,
          isSuperUserHub: superUsers.includes(u.id),
        }));

        // 4. Guardar
        setUsuarios(usuariosMarcados);

      } catch (error) {
        console.error("Error al cargar datos del hub:", error);
      }
    };

    cargarDatos();
  }, [hubId]);

  const toggleHasField = async (fieldName, value) => {
    try {
      const hubDocRef = doc(db, 'private-hubs', hubId);
      await setDoc(hubDocRef, { [fieldName]: !value }, { merge: true });
    } catch (error) {
      console.error("Error guardando configuración:", error);
      setMensaje("Error guardando configuración.");
    }
  };

  const toggleSuperUserHub = async (userId, checked) => {
    try {
      const hubDocRef = doc(db, 'private-hubs', hubId);
      await updateDoc(hubDocRef, {
        superUsers: checked ? arrayUnion(userId) : arrayRemove(userId),
      });
      setUsuarios(prev =>
        prev.map(user =>
          user.id === userId ? { ...user, isSuperUserHub: checked } : user
        )
      );
      console.log(`Usuario ${userId} ${checked ? 'añadido' : 'eliminado'} de superUsers.`);
    } catch (error) {
      console.error("Error al actualizar superusuario del hub:", error);
      alert("Error al actualizar superusuario del hub.");
    }
  };

  const crearUsuario = async () => {
    try {
      const user = auth.currentUser;
      if (!user) {
        setMensaje("No hay usuario autenticado");
        return;
      }

      await user.getIdToken(true);

      const crearUsuarioFn = httpsCallable(functions, 'crearUsuario');
      await crearUsuarioFn({
        email: correo,
        hubId: hubId
      });

      await sendPasswordResetEmail(auth, correo);

      setMensaje(`Usuario creado. Se envió correo para cambiar contraseña.`);
      alert(`Usuario creado. Se envió correo para cambiar contraseña.`);
      setCorreo('');
      window.location.reload();
    } catch (error) {
      console.error("Error al crear usuario:", error);

      if (error.code === 'functions/internal' && error.message.includes('already in use')) {
        try {
          const usuariosRef = collection(db, 'usuarios');
          const q = query(usuariosRef, where('email', '==', correo));
          const querySnapshot = await getDocs(q);

          if (!querySnapshot.empty) {
            const userDoc = querySnapshot.docs[0];
            const userData = userDoc.data();

            let hubs = userData.hubId || [];
            if (!Array.isArray(hubs)) {
              hubs = [hubs];
            }

            if (!hubs.includes(hubId)) {
              hubs.push(hubId);
            }

            await setDoc(userDoc.ref, { hubId: hubs }, { merge: true });

            setMensaje(`Usuario existente añadido al hub.`);
            alert(`Usuario existente añadido al hub.`);
            setCorreo('');
            window.location.reload();

          } else {
            const usuariosRef = collection(db, 'usuarios');
            await setDoc(doc(usuariosRef), {
              email: correo,
              hubId: [hubId],
              permisos: {}
            });

            setMensaje(`Usuario existente añadido al hub y creado en base de datos.`);
            alert(`Usuario existente añadido al hub y creado en base de datos.`);
            setCorreo('');
            window.location.reload();
          }
        } catch (firestoreError) {
          console.error("Error al manejar usuario existente en Firestore:", firestoreError);
          setMensaje('Error al manejar usuario existente en la base de datos.');
          alert('Error al manejar usuario existente en la base de datos.');
        }

      } else if (error.code === 'functions/permission-denied') {
        setMensaje('No tienes permiso para crear usuarios.');
        alert('No tienes permiso para crear usuarios.');
      } else if (error.code === 'unauthenticated') {
        setMensaje('Debes iniciar sesión para crear usuarios.');
        alert('Debes iniciar sesión para crear usuarios.');
      } else {
        setMensaje('Error al crear usuario: ' + error.message);
        alert('Error al crear usuario: ' + error.message);
      }
    }
  };

  const enviarCambioContraseña = async (email, hubIds) => {
    await sendPasswordResetEmail(auth, email);
    alert("Correo de cambio de contraseña enviado al correo " + email);
  };

  const actualizarPermiso = async (userId, permisoClave, nivelAcceso) => {
    try {
      const userDocRef = doc(db, 'usuarios', userId);
      const userSnap = await getDoc(userDocRef);
      const userData = userSnap.data();
      const permisosAnteriores = userData.permisos || {};
      const permisosHub = permisosAnteriores[hubId] || {};

      const nuevosPermisosHub = { ...permisosHub, [permisoClave]: nivelAcceso };
      const nuevosPermisos = { ...permisosAnteriores, [hubId]: nuevosPermisosHub };

      await setDoc(userDocRef, { permisos: nuevosPermisos }, { merge: true });

      setUsuarios(prev =>
        prev.map(user =>
          user.id === userId ? { ...user, permisos: nuevosPermisos } : user
        )
      );
    } catch (error) {
      console.error("Error al actualizar permiso:", error);
      alert("Error al actualizar permiso del usuario.");
    }
  };

  return (
    <div className='lobby-container'>
      <div className="header-container">
        <header className="header">
          <h1 className="header-title">Configuración del hub: {hubName} --- Id del hub: {hubId}</h1>
          <div className='logout-button-container'>
            <button onClick={() => navigate(-1)} className="logout-button">
              ← Volver
            </button>
          </div>
        </header>
      </div>

      <div className="data-box acciones-form">
        <input
          type="email"
          placeholder="Correo electrónico"
          value={correo}
          onChange={(e) => setCorreo(e.target.value)}
          style={{ margin: '20px', marginBottom: "0px" }}
        />
        <button onClick={crearUsuario} className="seccion-btn">Crear Usuario</button>
        {mensaje && <p style={{ marginLeft: "20px" }}>{mensaje}</p>}

        <div className="user-list" style={{ marginLeft: '20px' }}>
          <table>
            <thead>
              <tr>
                <th>Correo</th>
                {PERMISOS_DISPONIBLES.map(({ clave, label }) => (
                  <th key={clave}>{label}</th>
                ))}
                <th>Acciones</th>
                <th>S.U. Hub</th>
              </tr>
            </thead>
            <tbody>
              {usuarios
                .sort((a, b) => {
                  const aSuper = a.hubId?.includes("__superadmin__") ? 0 : 1;
                  const bSuper = b.hubId?.includes("__superadmin__") ? 0 : 1;
                  return aSuper - bSuper;
                })
                .map(user => (
                  <tr key={user.id}>
                    <td>{user.email}</td>
                    {PERMISOS_DISPONIBLES.map(({ clave }) => {
                      if (user.hubId?.includes("__superadmin__")) {
                        return (
                          <td key={clave} style={{ fontWeight: 'bold', color: 'green' }}>
                            Acceso total
                          </td>
                        );
                      }
                      return (
                        <td key={clave}>
                          <select
                            value={user.permisos?.[hubId]?.[clave] ?? 'sin_acceso'}
                            onChange={(e) => actualizarPermiso(user.id, clave, e.target.value)}
                            disabled={!user.hubId?.includes(hubId)}
                          >
                            <option value="sin_acceso">Sin acceso</option>
                            <option value="lectura">Lectura</option>
                            <option value="lectura_escritura">Lectura y Escritura</option>
                          </select>
                        </td>
                      );
                    })}
                    <td>
                      <button
                        onClick={() => enviarCambioContraseña(user.email, user.hubId || [])}
                      >
                        Cambiar Contraseña
                      </button>
                    </td>
                    <td>
                      <input
                        type="checkbox"
                        checked={user.isSuperUserHub || false}
                        onChange={(e) => toggleSuperUserHub(user.id, e.target.checked)}
                      />
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>

        <div className='toggles-container'>
          <div style={{ marginTop: '30px', marginLeft: '20px' }}>
            <label style={{ fontWeight: '600', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={hasCalendar}
                onChange={() => {
                  toggleHasField('hasCalendar', hasCalendar);
                  setHasCalendar(!hasCalendar);
                }}
                style={{ marginRight: '8px' }}
                className="labelText"
              />
              Activar calendario para este hub
            </label>
          </div>

          <div style={{ marginTop: '30px', marginLeft: '20px' }}>
            <label style={{ fontWeight: '600', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={hasMap}
                onChange={() => {
                  toggleHasField('hasMap', hasMap);
                  setHasMap(!hasMap);
                }}
                style={{ marginRight: '8px' }}
                className="labelText"
              />
              Activar mapa societario para este hub
            </label>
          </div>

          <div style={{ marginTop: '30px', marginLeft: '20px' }}>
            <label style={{ fontWeight: '600', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={hasDocs}
                onChange={() => {
                  toggleHasField('hasDocs', hasDocs);
                  setHasDocs(!hasDocs);
                }}
                style={{ marginRight: '8px' }}
                className="labelText"
              />
              Activar documentación para este hub
            </label>
          </div>

          <div style={{ marginTop: '30px', marginLeft: '20px', marginBottom: '40px' }}>
            <label style={{ fontWeight: '600', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={hasPlans}
                onChange={() => {
                  toggleHasField('hasPlans', hasPlans);
                  setHasPlans(!hasPlans);
                }}
                style={{ marginRight: '8px' }}
                className="labelText"
              />
              Activar planes para este hub
            </label>
          </div>
          <div style={{ marginTop: '30px', marginLeft: '20px', marginBottom: '40px' }}>
            <label style={{ fontWeight: '600', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={hasComms}
                onChange={() => {
                  toggleHasField('hasComms', hasComms);
                  setHasComms(!hasComms);
                }}
                style={{ marginRight: '8px' }}
                className="labelText"
              />
              Activar comunicaciones para este hub
            </label>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HubConfig;
